import { ChangeDetectionStrategy, Component, effect, inject, Input, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule, CheckboxModule } from 'pu-core-library';
import { ModalCheckConditionUseFormGroup } from '@shared/components/modal/modal-check-condition-use/modal-check-condition-use.form-group';
import { UseConditionsService } from '@shared/services/use-conditions/use-conditions.service';
import { EnvironmentService } from '@core/index';
import { Store } from '@ngrx/store';
import { AuthActions, authFeature } from '@auth/store';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '@environment/index';
import { TypeDocumentUrl } from '@shared/constants';
import Keycloak from 'keycloak-js';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';

@Component({
  standalone: true,
  selector: 'app-modal-check-condition-use',
  imports: [CheckboxModule, RouterLink, ButtonModule, TranslatePipe, ReactiveFormsModule],
  templateUrl: './modal-check-condition-use.component.html',
  styleUrl: './modal-check-condition-use.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCheckConditionUseComponent implements OnInit {
  formGroup!: FormGroup;
  labelConfirm!: string;
  labelCancel!: string;
  description!: string;
  isConfirmed: boolean = false;
  @Input() requiredDocs!: { cgu: boolean; cggc: boolean; pc: boolean };

  destroy$ = new Subject<boolean>();
  cggc!: string | null;

  readonly staticWebUrl = environment.staticWebUrl;
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

  constructor(
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private keycloak: Keycloak,
    private useConditionsService: UseConditionsService,
    private env: EnvironmentService,
    private store: Store,
  ) {
    effect(() => {
      const keycloakEvent = this.keycloakSignal();

      if (keycloakEvent.type === KeycloakEventType.AuthSuccess) {
        const types = [TypeDocumentUrl.CGGC];
        types.forEach((type) => {
          this.store.dispatch(AuthActions.loadCurrentDocumentURL({ typeDocument: type }));
          this.subscribeToUrl(type);
        });
      }
    });
  }
  onClose(): void {
    this.keycloak.logout({ redirectUri: this.env.host });
  }
  confirm(): void {
    this.isConfirmed = true;
    this.useConditionsService.validateCgu(this.formGroup).subscribe(
      () => {
        this.store.dispatch(AuthActions.updateUserGeneralCondition());
        this.dynamicDialogRef.close();
      },
      (error) => {
        console.error('Erreur lors de la validation des CGU:', error);
      },
    );
  }

  async ngOnInit(): Promise<void> {
    if (this.dynamicDialogConfig && this.dynamicDialogConfig.data) {
      this.labelCancel = this.dynamicDialogConfig.data.labelCancel;
      this.labelConfirm = this.dynamicDialogConfig.data.labelConfirm;
      this.description = this.dynamicDialogConfig.data.description;
      this.requiredDocs = this.dynamicDialogConfig.data.requiredDocs;
    }

    this.formGroup = new ModalCheckConditionUseFormGroup(this.requiredDocs);

    this.dynamicDialogRef.onClose.subscribe(() => {
      if (!this.isConfirmed) {
        this.keycloak.logout({ redirectUri: this.env.host });
      }
    });
  }

  get hasAnyDocToValidate(): boolean {
    return this.requiredDocs?.cgu || this.requiredDocs?.cggc || this.requiredDocs?.pc;
  }

  private subscribeToUrl(type: TypeDocumentUrl): void {
    this.store
      .select(authFeature.selectDocumentUrlByType(type))
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        const fullUrl = url ? `${this.staticWebUrl}${url}` : null;
        if (type === TypeDocumentUrl.CGGC) this.cggc = fullUrl;
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
